import React, { useMemo } from 'react';
import { injectIntl, IntlShape, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { RouteComponentProps, useLocation, withRouter } from 'react-router-dom';
import { Tab } from 'semantic-ui-react';
import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import { formatMessageHelper, FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { DataSearchResultTabParam, getDataSearchResultPath, getMainDataPath } from '../dataExplorerRouterPaths';
import { parseFilterQuery } from '../Filters/queryTransformers';
import { DatasetsTab, FieldsTab } from './SearchResultTabs';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { DataExplorerLink, useDataExplorerHistory } from '../dataExplorerHistoryContext';
import { addModifiers } from '../../../shared/helpers';

interface DatasetRouteProps {
  tab?: DataSearchResultTabParam;
}

function getTabPanes(intl: IntlShape) {
  return [
    { menuItem: formatMessageHelper(intl, 'dataExplorer.tabs.fields'), render: () => <FieldsTab /> },
    { menuItem: formatMessageHelper(intl, 'dataExplorer.tabs.datasets'), render: () => <DatasetsTab /> },
  ];
}

const idTabMap = {
  '0': 'data-fields',
  '1': 'data-sets',
} as const;

const tabIdMap = {
  'data-fields': '0',
  'data-sets': '1',
} as const;

function DataSearchResult({ match, intl }: RouteComponentProps<DatasetRouteProps> & InjectedIntlProps) {
  const history = useDataExplorerHistory();
  const tab = match.params.tab ?? idTabMap[0];

  const tabIndex = tabIdMap[tab];
  const location = useLocation();
  const queryString = location.search;
  const { search } = useMemo(() => parseFilterQuery(queryString), [queryString]) as { search?: string };

  const onTabChange = (activeTabId?: '0' | '1') => {
    const tab = idTabMap[activeTabId ?? tabIdMap['data-fields']] as DataSearchResultTabParam;
    history.push(getDataSearchResultPath({ tab, searchParam: search }));
  };

  const trackEvent = useDataExplorerTrackCustomEvent();

  return (
    <div className="data-main">
      <div className={addModifiers('data__header-container', ['search-fields'])}>
        <div className="data-breadcrumbs">
          <Breadcrumbs
            items={[
              {
                link: getMainDataPath(),
                name: <FormattedMessageHelper id="dataExplorer.title" />,
                LinkComponent: DataExplorerLink,
                onClickCapture: () => {
                  trackEvent('de_click_breadcrumb_main');
                },
              },
              {
                name: <FormattedMessageHelper id={`dataExplorer.breadcrumbs.searchResult`} />,
              },
            ]}
          />
        </div>
      </div>
      <Tab onTabChange={(_, data) => onTabChange(data.activeIndex as any)} activeIndex={tabIndex} panes={getTabPanes(intl)} />
    </div>
  );
}

export default withRouter(injectIntl(DataSearchResult));
